import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Package, Calendar, Tag, CheckCircle2, XCircle, FileText, ArrowRight, BookOpen, Palette,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ServiceHero, { Highlight } from "./ServiceHero";
import IncludesExcludes from "./IncludesExcludes";
import ProductCard from "./ProductCard";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}
function AnimateIn({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(30px)",
      transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>{children}</div>
  );
}

const includesList = [
  "Logo principal + variantes (horizontal, vertical, isotipo y versión monocromática)",
  "Archivos en todos los formatos (SVG, PNG, JPG, PDF)",
  "Paleta de colores corporativa (con códigos HEX, RGB y CMYK)",
  "Tipografías corporativas (principal y secundaria)",
  "Brandbook / manual de marca con usos correctos e incorrectos, espaciados y aplicaciones",
  "Hasta 2-3 propuestas iniciales de logo",
  "Hasta 2 rondas de revisión sobre la propuesta elegida",
];

const needsList = [
  "El nombre exacto de tu marca",
  "Los valores y personalidad de tu negocio",
  "Tu sector y tu competencia",
  "Referencias visuales (marcas o logos que te inspiren)",
  "Preferencias de color o estilo, si las tienes",
];

const notIncluded = [
  "Naming (creación del nombre de la marca)",
  "Papelería completa (tarjetas, sobres, facturas, etc.)",
  "Diseño de packaging",
  "Aplicaciones extensas de marca (rotulación, vehículos, uniformes)",
  "Registro legal de la marca (es un trámite jurídico aparte)",
  "Más de 3 propuestas iniciales o más de 2 rondas de revisión",
];

const steps = [
  { n: 1, title: "Contratas el servicio", desc: "Pago único de 500€." },
  { n: 2, title: "Briefing de marca", desc: "Nos cuentas tus valores, sector y referencias." },
  { n: 3, title: "Diseñamos propuestas", desc: "Hasta 3 propuestas iniciales de logo." },
  { n: 4, title: "Eliges y refinamos", desc: "Hasta 2 rondas de revisión sobre la elegida." },
  { n: 5, title: "Recibes tu marca", desc: "Logo, variantes, archivos y brandbook completo." },
];

const BrandingDemo = () => {
  return (
    <div className="space-y-28 overflow-hidden">
      <ServiceHero
        eyebrow="Servicio puntual · Pago único"
        title={<>Branding e <Highlight>Identidad de Marca</Highlight></>}
        description={<>Una marca con personalidad, coherente y profesional. Diseñamos tu logo y todo el universo visual que hará que tu negocio destaque y se recuerde.</>}
        includes={["500€ pago único", "Entrega en 14-21 días", "Brandbook completo incluido"]}
        primaryCta={{ label: "Crear mi marca", href: "/#contact" }}
        secondaryCta={{ label: "Ver qué incluye", href: "#incluye" }}
      />

      <IncludesExcludes
        includes={[
          "Logo principal + variantes (horizontal, vertical, isotipo, monocromático)",
          "Archivos en todos los formatos (SVG, PNG, JPG, PDF)",
          "Paleta de colores (HEX, RGB, CMYK)",
          "Tipografías corporativas",
          "Brandbook / manual de marca completo",
          "<strong>Diseño de tarjeta de visita</strong>",
          "<strong>3 propuestas iniciales de logo</strong>",
          "Hasta 2 rondas de revisión sobre la elegida",
        ]}
        excludes={[
          "Impresión de las tarjetas (solo diseño)",
          "Naming (creación del nombre)",
          "Papelería completa",
          "Packaging",
          "Registro legal de la marca",
          "Más de 3 propuestas o más de 2 rondas",
        ]}
      />

      {/* 3 COLUMNAS */}
      <section>
        <AnimateIn>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: <Package className="w-6 h-6" />, title: "Qué incluye", items: ["Logo + variantes", "Brandbook completo", "Paleta de colores", "Tipografías corporativas"] },
              { icon: <Calendar className="w-6 h-6" />, title: "Plazo de entrega", items: ["Entre 14 y 21 días", "Desde el briefing de marca"] },
              { icon: <Tag className="w-6 h-6" />, title: "Inversión", items: ["500€ (IVA incluido)", "Pago único", "Todos los archivos incluidos"] },
            ].map((c, i) => (
              <Card key={i} className="p-6 hover:border-primary/30 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">{c.icon}</div>
                <h4 className="font-semibold mb-3">{c.title}</h4>
                <ul className="space-y-2">
                  {c.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 size={14} className="text-primary shrink-0 mt-0.5" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </AnimateIn>
      </section>

      {/* INCLUYE */}
      <section id="incluye">
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Alcance del servicio</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Todo lo que incluye tu identidad de marca</h3>
            <p className="text-muted-foreground">Un paquete completo de identidad visual, listo para aplicar en todos tus canales.</p>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-3">
          {includesList.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.03}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                <CheckCircle2 size={18} className="text-primary shrink-0 mt-0.5" />
                <span className="text-sm">{item}</span>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* BRANDBOOK DESTACADO */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto rounded-3xl p-8 md:p-12 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30 shadow-glow">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <BookOpen className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold mb-3">Te entregamos un brandbook, no solo un logo</h3>
                <p className="text-muted-foreground mb-3">
                  Un logo suelto no es una marca. Por eso te entregamos un <strong className="text-foreground">brandbook completo</strong>: el manual que define cómo se usa tu identidad visual en cualquier soporte.
                </p>
                <p className="text-muted-foreground">
                  Incluye los usos correctos e incorrectos de tu logo, los espaciados, tu paleta de colores exacta y tus tipografías. Así tu marca se ve <strong className="text-foreground">siempre coherente y profesional</strong>, la use quien la use y donde se use.
                </p>
              </div>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* QUÉ NECESITAMOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Antes de empezar</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Qué necesitamos para empezar</h3>
            <p className="text-muted-foreground">Para crear una marca que te represente de verdad, necesitamos que nos cuentes:</p>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto space-y-3">
          {needsList.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.04}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <FileText size={16} />
                </div>
                <span className="text-sm pt-1">{item}</span>
              </div>
            </AnimateIn>
          ))}
          <AnimateIn>
            <p className="text-sm text-muted-foreground text-center pt-4 italic">
              El plazo de entrega (14-21 días) empieza a contar tras completar el briefing de marca.
            </p>
          </AnimateIn>
        </div>
      </section>

      {/* QUÉ NO INCLUYE */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="outline" className="mb-4 border-destructive/40 text-destructive">Sin sorpresas</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Qué no incluye este servicio</h3>
            <p className="text-muted-foreground">Para que todo quede claro desde el principio, estos puntos quedan fuera del servicio:</p>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto grid gap-3">
          {notIncluded.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.04}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                <XCircle size={18} className="text-destructive shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground">{item}</span>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* CÓMO TRABAJAMOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Proceso</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Cómo trabajamos</h3>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto space-y-4">
          {steps.map((s, i) => (
            <AnimateIn key={s.n} delay={i * 0.06}>
              <div className="flex items-start gap-5 p-5 rounded-2xl bg-card border border-border hover:border-primary/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent text-primary-foreground flex items-center justify-center font-bold text-lg shrink-0">
                  {s.n}
                </div>
                <div>
                  <h4 className="font-semibold mb-1">{s.title}</h4>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </div>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* EJEMPLOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Portfolio</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Marcas que hemos diseñado</h3>
            <p className="text-muted-foreground">Una muestra de identidades de marca que hemos creado para nuestros clientes.</p>
          </div>
        </AnimateIn>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, idx) => (
            <AnimateIn key={idx} delay={idx * 0.05}>
              <Card className="aspect-square flex items-center justify-center bg-gradient-to-br from-primary/5 to-accent/5 hover:border-primary/30 hover:shadow-lg transition-all group overflow-hidden">
                <Palette className="w-10 h-10 text-primary/30 group-hover:scale-110 transition-transform" />
              </Card>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* PRODUCT CARD - COMPRA ONLINE */}
      <ProductCard
        title="Branding e Identidad de Marca"
        description="Logo, variantes, paleta, tipografías y brandbook completo. Entrega en 14-21 días."
        price="500€"
        features={[
          "Logo principal + variantes (horizontal, vertical, isotipo, monocromática)",
          "Archivos en SVG, PNG, JPG y PDF",
          "Paleta de colores (HEX, RGB, CMYK)",
          "Brandbook completo con usos y aplicaciones",
          "Hasta 3 propuestas iniciales y 2 rondas de revisión",
        ]}
        stripeUrl="https://buy.stripe.com/00w28reXkePvg5ZgfY0480o"
      />

      {/* CTA FINAL */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto text-center rounded-3xl p-10 md:p-16 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30 shadow-glow">
            <h3 className="text-3xl md:text-5xl font-bold mb-4">¿Listo para tener una marca que se recuerde?</h3>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Pago único de 500€, con logo, variantes y brandbook completo. La tienes lista en 21 días.
            </p>
            <a href="/#contact" className="btn-pill bg-primary text-primary-foreground hover:shadow-glow px-8 py-4 inline-flex items-center gap-2">
              Crear mi marca
              <ArrowRight size={16} />
            </a>
          </div>
        </AnimateIn>
      </section>
    </div>
  );
};

export default BrandingDemo;
